/* eslint-disable no-console */
import { useCallback, useEffect, useState, useRef } from 'react'
import {
  Button,
  Dropdown,
  Popover,
  List,
  ListItem,
  Divider,
} from '@zoom/react-ui'
import { IconPlus, IconInformation } from '@zoom/icons-react'
import Docs from '../icons/Google/Docs'
import Slides from '../icons/Google/Slides'
import Sheets from '../icons/Google/Sheets'
import Word from '../icons/OneDrive/Word'
import Excel from '../icons/OneDrive/Excel'
import Powerpoint from '../icons/OneDrive/Powerpoint'
import ProfilePic from './ProfilePic'
import {
  useAppDispatch,
  useAppSelector,
  useSelectRunningContext,
  useSelectAuth,
  useSelectMobileDeviceInfo,
} from '../hooks'
import {
  MS_FILE_EXT_EXCEL,
  MS_FILE_EXT_DOC,
  MS_FILE_EXT_PPT,
} from '../constants'
import {
  selectApp,
  selectChat,
  selectNotificationsAvailable,
  setIsGeneralSpinner,
} from '../app/root'
import { setDialog } from '../app/dialog'
import {
  useLogoutMutation,
  useCreateFileMSMutation,
  useCreateFileGoogleMutation,
} from '../app/appApiQueries'
import NotificationPreferences from './NotificationPreferences'

const TopBar = () => {
  const dispatch = useAppDispatch()
  const app = useAppSelector(selectApp)
  const auth = useSelectAuth(app)
  const [logout] = useLogoutMutation({
    fixedCacheKey: 'logout',
  })
  const [switchAccount] = useLogoutMutation({
    fixedCacheKey: 'switchAccount',
  })
  const email = auth?.thirdPartyUserInfo?.email || ''
  const driveId = auth?.extra?.driveId
  const [createFileGoogle] = useCreateFileGoogleMutation()
  const [createFileMS] = useCreateFileMSMutation()
  const [isOpen, setIsOpen] = useState(false)
  const popoverRef = useRef(null)
  const runningContext = useSelectRunningContext()
  const chat = useAppSelector(selectChat)
  const notificationsAvailable = useAppSelector(selectNotificationsAvailable)
  const { isMobileDevice } = useSelectMobileDeviceInfo()

  const headerText =
    app === 'Microsoft'
      ? 'Select a document'
      : runningContext === 'inMainClient' || runningContext === 'inChat'
        ? 'Open a document'
        : 'Collaborate on a document'
  const newButtonText =
    runningContext === 'inMainClient' || runningContext === 'inChat'
      ? 'New document'
      : 'New'

  const newButtonGoogle = useCallback(
    (type) => {
      if (
        runningContext === 'inMainClient' ||
        runningContext === 'inChat' ||
        isMobileDevice
      ) {
        dispatch(setIsGeneralSpinner(true))
        createFileGoogle({
          drive: app,
          fileName: 'Untitled Document',
          fileType: type,
          share: false,
        })
          .unwrap()
          .then((data) => zoomSdk.openUrl({ url: data.fileUrl }))
          .catch((error) => {
            console.error('create API error: ', error)
          })
          .finally(() => {
            dispatch(setIsGeneralSpinner(false))
          })
      } else if (runningContext === 'inCollaborate') {
        dispatch(
          setDialog({ type: 'collaborationNoShareNew', props: { file: type } })
        )
      } else {
        dispatch(
          setDialog({ type: 'fileShareGoogleNew', props: { file: type } })
        )
      }
    },
    [app, createFileGoogle, dispatch, isMobileDevice, runningContext]
  )

  // ============================= Start V3 =============================
  // Uncomment me to go to back to native collaboration
  // const newButtonMS = useCallback(
  //   (type) => {
  //     dispatch(setIsGeneralSpinner(true))
  //     createFileMS({
  //       drive: app,
  //       fileName: 'Untitled Document',
  //       fileType: type,
  //       driveId,
  //     }).finally(() => {
  //       dispatch(setIsGeneralSpinner(false))
  //     })
  //   },
  //   [app, createFileMS, dispatch, driveId]
  // )
  // ============================= End V3 =============================

  // ============================= Start V3 =============================
  // Delete me to go to back to native collaboration
  const newButtonMS = useCallback(
    (type) => {
      if (runningContext === 'inMainClient' || runningContext === 'inChat') {
        dispatch(setIsGeneralSpinner(true))
        createFileMS({
          drive: app,
          fileName: 'Untitled Document',
          fileType: type,
          driveId,
        })
          .unwrap()
          .then((data) => zoomSdk.openUrl({ url: data.fileUrl }))
          .catch((error) => {
            console.error('create API error: ', error)
          })
          .finally(() => {
            dispatch(setIsGeneralSpinner(false))
          })
      } else if (runningContext === 'inCollaborate') {
        dispatch(
          setDialog({ type: 'collaborationNoShareNew', props: { file: type } })
        )
      } else {
        dispatch(setIsGeneralSpinner(true))
        createFileMS({
          drive: app,
          fileName: 'Untitled Document',
          fileType: type,
          driveId,
        })
          .unwrap()
          .then(() => {
            dispatch(
              setDialog({ type: 'fileShareMSNew', props: { file: type } })
            )
          })
          .catch((error) => {
            console.error('create API error: ', error)
          })
          .finally(() => {
            dispatch(setIsGeneralSpinner(false))
          })
      }
    },
    [app, createFileMS, driveId, dispatch, runningContext]
  )
  // ============================= End V3 =============================

  const handleNewButton = async (type) => {
    app === 'Google' ? newButtonGoogle(type) : newButtonMS(type)
  }

  const handleSwitchAccount = async () => {
    dispatch(setIsGeneralSpinner(true))
    await switchAccount(app)
      .unwrap()
      .catch((error) => {
        console.error('switch account error: ', error)
      })
      .finally(() => {
        dispatch(setIsGeneralSpinner(false))
      })
    setIsOpen(false)
  }

  const handleDisconnectAccount = async () => {
    dispatch(setIsGeneralSpinner(true))
    await logout(app)
      .unwrap()
      .catch((error) => {
        console.error('disconnect account error: ', error)
      })
      .finally(() => {
        dispatch(setIsGeneralSpinner(false))
      })
    setIsOpen(false)
  }

  const handleClickOutside = useCallback(
    (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    },
    [popoverRef]
  )

  useEffect(() => {
    // Note: no dropdown or popover will close if the iframe is clicked
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [handleClickOutside])

  const newItems = {
    Microsoft: [
      {
        label: <span className='font-sans'>Word</span>,
        key: MS_FILE_EXT_DOC,
        icon: <Word />,
      },
      {
        label: <span className='font-sans'>Excel</span>,
        key: MS_FILE_EXT_EXCEL,
        icon: <Excel />,
      },
      {
        label: <span className='font-sans'>Powerpoint</span>,
        key: MS_FILE_EXT_PPT,
        icon: <Powerpoint />,
      },
    ],
    Google: [
      {
        label: <span className='font-sans'>Google Docs</span>,
        key: MS_FILE_EXT_DOC,
        icon: <Docs />,
      },
      {
        label: <span className='font-sans'>Google Sheets</span>,
        key: MS_FILE_EXT_EXCEL,
        icon: <Sheets />,
      },
      {
        label: <span className='font-sans'>Google Slides</span>,
        key: MS_FILE_EXT_PPT,
        icon: <Slides />,
      },
    ],
  }

  const content = (
    <div ref={popoverRef}>
      <List divider={false}>
        <div className='w-full flex-col px-2 font-sans'>
          <div className='py-[6px] font-bold'>Connected {app} account:</div>
          <div className='truncate py-[6px]'>{email}</div>
        </div>
        <ListItem
          className='cursor-pointer'
          onClick={handleSwitchAccount}
        >
          <div className='flex items-center gap-2 font-sans'>
            Switch account
            <Popover
              width={240}
              content={`Switching account here will also switch ${app} account for other Zoom Workplace products.`}
              trigger='hover'
              placement='top'
            >
              <span>
                <IconInformation aria-label='Learn more about switching' />
              </span>
            </Popover>
          </div>
        </ListItem>
        <ListItem
          className='cursor-pointer'
          onClick={handleDisconnectAccount}
        >
          <div className='flex items-center gap-2 font-sans'>
            Disconnect account
            <Popover
              width={240}
              content={`Disconnecting account here will also disconnect ${app} account for other Zoom Workplace products.`}
              trigger='hover'
              placement='top'
            >
              <span>
                <IconInformation aria-label='Learn more about disconnect' />
              </span>
            </Popover>
          </div>
        </ListItem>
        {app === 'Google' && notificationsAvailable && (
          <>
            <Divider />
            <NotificationPreferences
              setOpen={setIsOpen}
              isChatEnabled={chat}
            />
          </>
        )}
      </List>
    </div>
  )

  return (
    <div className='flex h-[60px] items-center p-3 shadow-topBar'>
      <h1 className='text-base font-semibold'>{headerText}</h1>
      <div className='ml-auto flex items-center justify-center gap-2'>
        <Dropdown
          trigger='click'
          items={newItems[app]}
          onClick={(e) => handleNewButton(e.key)}
        >
          <Button
            type='primary'
            size='medium'
            startIcon={<IconPlus aria-hidden />}
          >
            {newButtonText}
          </Button>
        </Dropdown>

        <Popover
          maxWidth={318}
          content={content}
          placement='bottom-end'
          arrow={false}
          open={isOpen}
        >
          <Button
            variant='icon'
            size='medium'
            type='tertiary'
            onClick={() => setIsOpen(true)}
          >
            <ProfilePic aria-hidden='true' />
          </Button>
        </Popover>
      </div>
    </div>
  )
}
export default TopBar
